import axios from 'axios';

/** One entry of a template: a field, a list of rows or values, or a keyed sub-record. */
export interface TemplateEntry {
  path: string;
  label: string;
  type?: string;
  values?: string[];
  required?: boolean;
  instruction?: string;
  citation?: 'REQUIRED' | 'OPTIONAL' | 'NONE';
  booking_key?: string;
  review_group?: string;
  topic?: string;
  validation?: { min?: number; max?: number; pattern?: string; max_length?: number; enum?: string[] };
  item_fields?: Array<{ name: string; label: string; type: string; required?: boolean; values?: string[] }>;
  item_type?: string;
}

export interface CrossFieldCheck {
  rule: 'DATE_BEFORE' | 'LTE' | 'SUM_EQUALS' | 'REQUIRED_IF';
  description?: string;
  left?: string;
  right?: string;
  terms?: string[];
  total?: string;
  field?: string;
  when?: string;
  equals?: unknown;
  tolerance?: number;
}

export interface TemplateJson {
  schema_version: number;
  fields: TemplateEntry[];
  arrays?: TemplateEntry[];
  objects?: TemplateEntry[];
  cross_field_checks?: CrossFieldCheck[];
  review_groups?: string[];
}

export type TemplateStatus = 'DRAFT' | 'PROPOSED' | 'ACTIVE' | 'RETIRED';

export interface TemplateVersion {
  id: number;
  documentType: string;
  version: number;
  status: TemplateStatus;
  assetClass: string;
  label: string;
  description: string | null;
  filenameHints: string[];
  templateJson: TemplateJson;
  fingerprint: string;
  supersedesId: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
  contributors: string[];
  proposedBy: string | null;
  proposedAt: string | null;
  activatedBy: string | null;
  activatedAt: string | null;
  retiredBy: string | null;
  retiredAt: string | null;
  statusNote: string | null;
}

export interface TemplateProblem {
  pointer: string;
  message: string;
}

export interface VersionView {
  template: TemplateVersion;
  problems: TemplateProblem[];
  nextAction: string;
}

export interface BookingContract {
  documentType: string;
  assetClass: string;
  bookingMethod: string;
  allowedKeys: string[];
  requiredKeys: string[];
  aliases: Record<string, string[]>;
  evidenceKeys: string[];
}

export interface EntryChange {
  path: string;
  changes: Array<{ property: string; from: unknown; to: unknown }>;
}

export interface SectionDiff {
  added: TemplateEntry[];
  removed: TemplateEntry[];
  changed: EntryChange[];
}

export interface TemplateDiff {
  from: { id: number; version: number; status: string } | null;
  to: { id: number; version: number; status: string };
  header: Record<string, { from: unknown; to: unknown }>;
  fields: SectionDiff;
  arrays: SectionDiff;
  objects: SectionDiff;
  crossFieldChecks: { added: CrossFieldCheck[]; removed: CrossFieldCheck[] };
  reviewGroups?: { from: string[]; to: string[] };
  bookingKeys: { added: string[]; removed: string[] };
  unchanged: boolean;
}

export interface DryRunError {
  rule: string;
  message: string;
  source: 'APPENDIX' | 'MODEL' | 'NOT_STATED';
  pointer: string;
}

export interface DryRunField {
  path: string;
  label: string;
  section: 'fields' | 'arrays' | 'objects';
  kind: string;
  review_group: string | null;
  required: boolean;
  citation_rule: string | null;
  booking_key: string | null;
  stated: boolean;
  value: unknown;
  rows: number | null;
  citation: { page?: string | number; section?: string; clause?: string; excerpt?: string } | null;
  errors: DryRunError[];
}

export interface DryRun {
  run_id: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  document_type: string;
  document_name: string;
  template: { id: number; version: number; label: string };
  started_at: string;
  completed_at?: string;
  error?: string;
  validation?: { status: 'PASS' | 'FAIL' | 'INCOMPLETE'; error_count: number };
  fields?: DryRunField[];
  booking_changes?: Array<{ key: string; code_path: unknown; template: unknown; change: 'ADDED' | 'REMOVED' | 'CHANGED' }>;
  manager_decision?: { decision?: string; rationale?: string };
}

export interface CatalogEntry {
  documentType: string;
  assetClass: string;
  label: string;
  description: string | null;
  filenameHints: string[];
  version: number;
  activatedAt: string | null;
}

const BASE = '/api/document-templates';

export const listVersions = async (): Promise<VersionView[]> => (await axios.get<VersionView[]>(BASE)).data;
export const getVersion = async (id: number): Promise<VersionView> => (await axios.get<VersionView>(`${BASE}/${id}`)).data;
export const getContracts = async (): Promise<Record<string, BookingContract>> =>
  (await axios.get<Record<string, BookingContract>>(`${BASE}/booking-contracts`)).data;
export const getDiff = async (id: number): Promise<TemplateDiff> => (await axios.get<TemplateDiff>(`${BASE}/${id}/diff`)).data;

export const startFromDefault = async (documentType: string): Promise<VersionView> =>
  (await axios.post<VersionView>(`${BASE}/defaults/${encodeURIComponent(documentType)}`)).data;
export const draftFrom = async (id: number): Promise<VersionView> => (await axios.post<VersionView>(`${BASE}/${id}/draft`)).data;
export const saveDraft = async (id: number, body: {
  label?: string; description?: string; filenameHints?: string[]; templateJson?: TemplateJson;
}): Promise<VersionView> => (await axios.put<VersionView>(`${BASE}/${id}`, body)).data;
export const propose = async (id: number): Promise<VersionView> => (await axios.post<VersionView>(`${BASE}/${id}/propose`)).data;
export const activate = async (id: number): Promise<VersionView> => (await axios.post<VersionView>(`${BASE}/${id}/activate`)).data;
export const returnToDraft = async (id: number, reason: string): Promise<VersionView> =>
  (await axios.post<VersionView>(`${BASE}/${id}/return-to-draft`, { reason })).data;
export const retire = async (id: number, reason: string): Promise<VersionView> =>
  (await axios.post<VersionView>(`${BASE}/${id}/retire`, { reason })).data;

export const startDryRun = async (id: number, file: File): Promise<{ run_id: string; status: string }> => {
  const form = new FormData();
  form.append('file', file);
  return (await axios.post(`${BASE}/${id}/dry-run`, form, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
};
export const getDryRun = async (runId: string): Promise<DryRun> =>
  (await axios.get<DryRun>(`${BASE}/dry-runs/${encodeURIComponent(runId)}`)).data;

export const getCatalog = async (): Promise<CatalogEntry[]> =>
  (await axios.get<CatalogEntry[]>('/api/document-template-catalog')).data;

/** The server's own words for a refusal: problems listed, the agent's reason, or the error message. */
export const templateErrorMessage = (failure: unknown, fallback: string): string => {
  const data = (failure as { response?: { data?: { detail?: string; error?: string; message?: string; problems?: TemplateProblem[] } } })
    ?.response?.data;
  if (data?.problems?.length) {
    return `${data.error || fallback}: ${data.problems.map(p => p.message).join('; ')}`;
  }
  return data?.detail || data?.error || data?.message || fallback;
};

/** F-PCP-038: the dry runs made with a version (persisted by the agent), newest first. */
export interface DryRunSummary { run_id: string; status: string; document_name?: string; document_type?: string; started_at?: string; completed_at?: string; validation_status?: string | null; error_count?: number | null; booking_changes?: number | null; error?: string | null }
export const listDryRuns = async (id: number): Promise<DryRunSummary[]> => (await axios.get<DryRunSummary[]>(`${BASE}/${id}/dry-runs`)).data;
